import { useEffect, useState, type RefObject } from 'react';

interface Dimensions {
  width: number;
  height?: number;
}

/**
 * Hook to observe and track element dimensions using ResizeObserver
 *
 * @param ref - React ref to the element to observe
 * @param initialWidth - Initial width before measurement (default: 800)
 * @returns Current dimensions of the observed element
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { width } = useResizeObserver(containerRef);
 * ```
 */
export function useResizeObserver(ref: RefObject<HTMLElement | null>, initialWidth = 800): Dimensions {
  const [dimensions, setDimensions] = useState<Dimensions>({ width: initialWidth });

  useEffect(() => {
    const updateDimensions = () => {
      if (ref.current) {
        // Use clientWidth/clientHeight to get the actual content area
        // (excludes borders and scrollbars, includes padding)
        setDimensions({
          width: ref.current.clientWidth,
          height: ref.current.clientHeight,
        });
      }
    };

    updateDimensions();

    // Use ResizeObserver for more efficient dimension tracking
    if (typeof ResizeObserver !== 'undefined' && ref.current) {
      const observer = new ResizeObserver(updateDimensions);
      observer.observe(ref.current);
      return () => observer.disconnect();
    }

    // Fallback to window resize listener
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [ref]);

  return dimensions;
}
